import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle2, XCircle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface PendingApprovalProps {
  applicationType: 'agent' | 'agency' | 'developer';
}

const tableMap = {
  agent: 'agent_applications',
  agency: 'agency_applications',
  developer: 'developer_applications',
} as const;

const labelMap = {
  agent: 'Agent',
  agency: 'Agency',
  developer: 'Developer',
};

const applicationRouteMap = {
  agent: '/agent-application',
  agency: '/agency-application',
  developer: '/developer-application',
};

export function PendingApproval({ applicationType }: PendingApprovalProps) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: application, isLoading } = useQuery({
    queryKey: [`${applicationType}-application-status`, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(tableMap[applicationType] as any)
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(1);
      if (error) throw error;
      return (data as any[])?.[0] || null;
    },
    enabled: !!user,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // No application submitted yet
  if (!application) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-lg w-full">
          <CardContent className="py-12 text-center space-y-4">
            <FileText className="h-16 w-16 mx-auto text-muted-foreground" />
            <h2 className="text-xl font-bold">Complete Your {labelMap[applicationType]} Application</h2>
            <p className="text-muted-foreground">
              You registered as a {labelMap[applicationType].toLowerCase()} but haven't submitted your application yet. 
              Please complete and submit your application with the required documents to get started.
            </p>
            <Button onClick={() => navigate(applicationRouteMap[applicationType])} className="mt-4">
              Complete Application
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = {
    pending: {
      icon: Clock,
      color: 'bg-yellow-100 text-yellow-800',
      title: 'Application Under Review',
      description: `Your ${labelMap[applicationType].toLowerCase()} application is currently being reviewed by our team. This usually takes 1-3 business days.`,
    },
    rejected: {
      icon: XCircle,
      color: 'bg-red-100 text-red-800',
      title: 'Application Rejected',
      description: `Unfortunately, your ${labelMap[applicationType].toLowerCase()} application was not approved. Please review the notes below and resubmit.`,
    },
    approved: {
      icon: CheckCircle2,
      color: 'bg-green-100 text-green-800',
      title: 'Application Approved',
      description: 'Congratulations! Your application has been approved.',
    },
  };

  const config = statusConfig[application.status as keyof typeof statusConfig] || statusConfig.pending;
  const StatusIcon = config.icon;

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Card className="max-w-lg w-full">
        <CardContent className="py-12 text-center space-y-4">
          <StatusIcon className={`h-16 w-16 mx-auto ${application.status === 'pending' ? 'text-yellow-500' : application.status === 'rejected' ? 'text-red-500' : 'text-green-500'}`} />
          <h2 className="text-xl font-bold">{config.title}</h2>
          <Badge className={config.color}>{application.status.charAt(0).toUpperCase() + application.status.slice(1)}</Badge>
          <p className="text-muted-foreground">{config.description}</p>
          {application.reviewer_notes && (
            <div className="mt-4 p-4 bg-muted rounded-lg text-left">
              <div className="text-sm font-semibold mb-1">Reviewer Notes:</div>
              <p className="text-sm text-muted-foreground">{application.reviewer_notes}</p>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-4">
            Submitted {(() => {
              const days = Math.floor((Date.now() - new Date(application.created_at).getTime()) / (1000 * 60 * 60 * 24));
              if (days === 0) return 'today';
              if (days === 1) return '1 day ago';
              return `${days} days ago`;
            })()} ({new Date(application.created_at).toLocaleDateString()})
            {application.status === 'pending' && ' • Review usually takes 1-3 business days'}
          </p>
          {application.status === 'rejected' && (
            <Button onClick={() => navigate(applicationRouteMap[applicationType])} variant="outline" className="mt-2">
              Resubmit Application
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
